import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import mapboxgl from 'mapbox-gl'
import type { ImageSource } from 'mapbox-gl'
import { Radar } from 'lucide-react'
import type { NwsGeometry } from '@/lib/nws/types'
import { outerRings, geometryCentroid, centroid, type LatLon } from '@/lib/storm/geo'
import { compass, type StormMotion } from '@/lib/storm/motion'
import { useMapbox } from '@/features/map/useMapbox'
import { requestRender } from '@/lib/map/radarClient'
import { nearestSite } from '@/lib/map/sites'
import { fetchRadarIndex, radarTileTemplate } from '@/lib/storm/rainviewer'
import { hasMapbox } from '@/config'
import { fmtTime } from '@/lib/time'

/** RainViewer's global radar product is only served up to zoom 7 (fallback only). */
const RV_MAX_Z = 7
/** Re-pull the live volume on this cadence (the service renders on demand). */
const REFRESH_MS = 90 * 1000

/**
 * STORM-MODE radar hero — the NEAREST WSR-88D site rendered at FULL RESOLUTION by our
 * own radar-render service (real Level II → tat-radar.pal, geo-referenced EPSG:3857 PNG),
 * dropped onto a clean dark Mapbox basemap that makes reflectivity pop. The live NWS
 * warning polygon, the user pin, and the storm-motion arrow ride on top. Site auto-selects
 * to the storm itself (motion cell → polygon centroid → user). RainViewer is a raster
 * fallback only when the render service has nothing.
 */
export function RadarMap({
  lat,
  lon,
  geometry,
  polygonColor,
  motion,
}: {
  lat: number
  lon: number
  geometry: NwsGeometry | null
  polygonColor: string
  motion: StormMotion | null
}) {
  // Where the storm actually IS — its live cell (motion LOC), else the polygon center,
  // else the user. Drives both the nearest-site pick and the initial map center.
  const focus = useMemo<LatLon>(() => {
    const m = motion && motion.points.length ? centroid(motion.points) : null
    return m ?? geometryCentroid(geometry) ?? { lat, lon }
  }, [motion, geometry, lat, lon])

  const site = useMemo(() => nearestSite(focus.lat, focus.lon), [focus.lat, focus.lon])

  // `center`/`zoom` are the INITIAL view only (useMapbox captures them once); the map is
  // re-framed imperatively by the fitBounds effect below once the storm extent is known.
  const { containerRef, map, ready, error } = useMapbox({
    center: [focus.lon, focus.lat],
    zoom: 8,
    navControl: false,
  })

  // Our own nearest-site high-res render (primary).
  const renderQ = useQuery({
    queryKey: ['storm-render', site.id],
    queryFn: () => requestRender(site.id, 'ref', 0.5),
    enabled: hasMapbox,
    staleTime: 60 * 1000,
    refetchInterval: REFRESH_MS,
    retry: 0,
  })
  const render = renderQ.data ?? null
  const renderUnavailable = renderQ.isError

  // RainViewer fallback — only fetched if our render can't be had.
  const rvQ = useQuery({
    queryKey: ['storm-rainviewer'],
    queryFn: fetchRadarIndex,
    enabled: hasMapbox && renderUnavailable,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
  const rvFrame = useMemo(() => {
    const frames = rvQ.data?.frames ?? []
    const lastObserved = frames.map((f) => f.nowcast).lastIndexOf(false)
    return frames[lastObserved >= 0 ? lastObserved : frames.length - 1] ?? null
  }, [rvQ.data])

  const framedRef = useRef(false)
  const radarKeyRef = useRef<string | null>(null)
  const rvPathRef = useRef<string | null>(null)
  const pinRef = useRef<mapboxgl.Marker | null>(null)
  const arrowRef = useRef<mapboxgl.Marker | null>(null)

  // ── High-res render → image source + raster layer (below the polygon) ───────
  useEffect(() => {
    if (!map || !ready || !render) return
    const key = `${render.imageUrl}`
    const src = map.getSource('storm-radar-src') as ImageSource | undefined
    if (!src) {
      map.addSource('storm-radar-src', { type: 'image', url: render.imageUrl, coordinates: render.corners })
      map.addLayer(
        {
          id: 'storm-radar',
          type: 'raster',
          source: 'storm-radar-src',
          paint: { 'raster-opacity': 0.85, 'raster-resampling': 'nearest', 'raster-fade-duration': 0 },
        },
        map.getLayer('storm-poly-fill') ? 'storm-poly-fill' : undefined,
      )
      radarKeyRef.current = key
    } else if (key !== radarKeyRef.current && typeof src.updateImage === 'function') {
      src.updateImage({ url: render.imageUrl, coordinates: render.corners })
      radarKeyRef.current = key
    }
    // A live render arrived — drop any RainViewer fallback that was up.
    if (map.getLayer('storm-rv')) map.removeLayer('storm-rv')
    if (map.getSource('storm-rv-src')) map.removeSource('storm-rv-src')
    rvPathRef.current = null
  }, [map, ready, render])

  // ── RainViewer fallback → raster tile source (only when no render) ──────────
  // Rebuilt whenever RainViewer publishes a newer frame, so the fallback advances
  // instead of freezing on the first observed scan (a raster source's tile template
  // can't be mutated in place, so we swap the source+layer).
  useEffect(() => {
    if (!map || !ready || render || !renderUnavailable || !rvFrame || !rvQ.data) return
    if (map.getLayer('storm-rv') && rvPathRef.current === rvFrame.path) return
    if (map.getLayer('storm-rv')) map.removeLayer('storm-rv')
    if (map.getSource('storm-rv-src')) map.removeSource('storm-rv-src')
    const tiles = [radarTileTemplate(rvQ.data.host, rvFrame.path)]
    map.addSource('storm-rv-src', { type: 'raster', tiles, tileSize: 256, maxzoom: RV_MAX_Z })
    map.addLayer(
      { id: 'storm-rv', type: 'raster', source: 'storm-rv-src', paint: { 'raster-opacity': 0.8 } },
      map.getLayer('storm-poly-fill') ? 'storm-poly-fill' : undefined,
    )
    rvPathRef.current = rvFrame.path
  }, [map, ready, render, renderUnavailable, rvFrame, rvQ.data])

  // ── Warning polygon → translucent fill + bright stroke (above the radar) ────
  useEffect(() => {
    if (!map || !ready) return
    const feature: GeoJSON.Feature | null = geometry
      ? { type: 'Feature', properties: {}, geometry: geometry as GeoJSON.Geometry }
      : null
    const src = map.getSource('storm-poly-src') as mapboxgl.GeoJSONSource | undefined
    if (!feature) {
      if (map.getLayer('storm-poly-line')) map.removeLayer('storm-poly-line')
      if (map.getLayer('storm-poly-fill')) map.removeLayer('storm-poly-fill')
      if (src) map.removeSource('storm-poly-src')
      return
    }
    if (!src) {
      map.addSource('storm-poly-src', { type: 'geojson', data: feature })
      map.addLayer({ id: 'storm-poly-fill', type: 'fill', source: 'storm-poly-src', paint: { 'fill-color': polygonColor, 'fill-opacity': 0.12 } })
      map.addLayer({
        id: 'storm-poly-line',
        type: 'line',
        source: 'storm-poly-src',
        layout: { 'line-join': 'round' },
        paint: { 'line-color': polygonColor, 'line-width': 2.5 },
      })
    } else {
      src.setData(feature)
      map.setPaintProperty('storm-poly-fill', 'fill-color', polygonColor)
      map.setPaintProperty('storm-poly-line', 'line-color', polygonColor)
    }
  }, [map, ready, geometry, polygonColor])

  // ── User pin (white dot + pulse ring) ───────────────────────────────────────
  // Create the marker once, then just move it — Marker.addTo is a no-op once it's on
  // the map, so this never detaches/re-attaches the DOM node on a coordinate change.
  useEffect(() => {
    if (!map || !ready) return
    if (!pinRef.current) {
      const el = document.createElement('div')
      el.className = 'radar__pin'
      el.innerHTML = '<span class="radar__pin-dot"></span><span class="radar__pin-ring"></span>'
      pinRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
    }
    pinRef.current.setLngLat([lon, lat]).addTo(map)
  }, [map, ready, lat, lon])

  // ── Storm-motion arrow — anchored at the live cell, rotated toward heading ──
  useEffect(() => {
    if (!map || !ready) return
    const cell = motion && motion.points.length ? centroid(motion.points) : null
    if (!cell || !motion || motion.speedKt <= 0) {
      arrowRef.current?.remove()
      return
    }
    if (!arrowRef.current) {
      const el = document.createElement('div')
      el.className = 'radar__motion-arrow'
      el.innerHTML =
        '<svg viewBox="0 0 24 48" width="24" height="48" aria-hidden="true">' +
        '<line x1="12" y1="45" x2="12" y2="9" stroke-width="2.5" stroke-linecap="round"/>' +
        '<circle cx="12" cy="45" r="3"/>' +
        '<polygon points="12,3 6.5,13 17.5,13"/>' +
        '</svg>'
      arrowRef.current = new mapboxgl.Marker({ element: el, anchor: 'bottom', rotationAlignment: 'map' })
    }
    arrowRef.current.getElement().style.color = polygonColor
    arrowRef.current.setRotation(motion.towardDeg).setLngLat([cell.lon, cell.lat]).addTo(map)
  }, [map, ready, motion, polygonColor])

  // Tear the markers down only on unmount (not on every coord/motion change).
  useEffect(
    () => () => {
      pinRef.current?.remove()
      pinRef.current = null
      arrowRef.current?.remove()
      arrowRef.current = null
    },
    [],
  )

  // ── Frame the storm once: fit the polygon + user + cell, padded ─────────────
  useEffect(() => {
    if (!map || !ready || framedRef.current) return
    const pts: LatLon[] = [{ lat, lon }, ...outerRings(geometry).flat().map(([rLon, rLat]) => ({ lat: rLat, lon: rLon })), ...(motion?.points ?? [])]
    if (pts.length < 2) return
    framedRef.current = true
    const lats = pts.map((p) => p.lat)
    const lons = pts.map((p) => p.lon)
    map.fitBounds(
      [
        [Math.min(...lons), Math.min(...lats)],
        [Math.max(...lons), Math.max(...lats)],
      ],
      { padding: 48, duration: 0, maxZoom: 10 },
    )
  }, [map, ready, geometry, motion, lat, lon])

  const status = !hasMapbox || error
    ? 'Map unavailable'
    : !ready
      ? 'Locating storm…'
      : render
        ? null
        : renderUnavailable && rvFrame
          ? null
          : renderQ.isError
            ? `No radar for ${site.id}`
            : 'Rendering radar…'

  const sourceLabel = render ? render.site : renderUnavailable && rvFrame ? 'RainViewer' : site.id
  const scanLabel = render ? `${fmtTime(render.scan)} scan` : renderUnavailable && rvFrame ? fmtTime(new Date(rvFrame.time * 1000).toISOString()) : null

  return (
    <div className="radar">
      <div className="radar__canvas" ref={containerRef} />

      <div className="radar__hud">
        <span className="radar__src">
          <Radar size={12} /> {sourceLabel}
          {scanLabel && <em className="radar__scan num">{scanLabel}</em>}
        </span>
        {motion && motion.speedKt > 0 && (
          <span className="radar__motion num">
            → {compass(motion.towardDeg)} · {motion.speedMph} mph
          </span>
        )}
      </div>

      {status && <div className="radar__loading">{status}</div>}
    </div>
  )
}
